import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Divider,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import CameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

const AdminPosterGenerator = () => {
  const [dramas, setDramas] = useState([]);
  const [selectedDrama, setSelectedDrama] = useState('');
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisode, setSelectedEpisode] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [generatedPosters, setGeneratedPosters] = useState([]);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // 加载剧集数据
  useEffect(() => {
    loadDramas();
  }, []);

  const loadDramas = async () => {
    try {
      const response = await fetch('/api/dramas');
      if (response.ok) {
        const data = await response.json();
        setDramas(data.data || []);
      } else {
        throw new Error('Failed to load dramas');
      }
    } catch (error) {
      console.error('Error loading dramas:', error);
      setSnackbarMessage('无法加载剧集数据');
      setSnackbarSeverity('error');
    }
  };

  // 当选择剧集时，更新可用的剧集列表
  const handleDramaChange = (event) => {
    const dramaId = event.target.value;
    setSelectedDrama(dramaId);
    setSelectedEpisode('');
    
    if (dramaId) {
      const drama = dramas.find(d => d.id === dramaId);
      if (drama && drama.episodes) {
        setEpisodes(drama.episodes);
      } else {
        setEpisodes([]);
      }
    } else {
      setEpisodes([]);
    }
    
    setThumbnailUrl('');
    setCurrentTime(0);
    setIsPlaying(false);
  };

  // 当选择集数时，更新视频源
  const handleEpisodeChange = (event) => {
    const episodeIndex = event.target.value;
    setSelectedEpisode(episodeIndex);
    
    if (episodeIndex !== '' && episodeIndex !== null && episodeIndex !== undefined) {
      setThumbnailUrl('');
      setCurrentTime(0);
      setIsPlaying(false);
    }
  };

  // 播放/暂停视频
  const handlePlayPause = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // 前一帧
  const handlePreviousFrame = () => {
    if (!videoRef.current) return;
    
    const newTime = Math.max(0, currentTime - 0.1);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  // 后一帧
  const handleNextFrame = () => {
    if (!videoRef.current) return;
    
    const newTime = Math.min(videoRef.current.duration, currentTime + 0.1);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  // 拍照生成海报
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsGenerating(true);
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // 设置canvas尺寸与视频一致
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      // 绘制当前帧到canvas
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      
      // 生成截图URL
      const screenshotUrl = canvas.toDataURL('image/jpeg');
      setThumbnailUrl(screenshotUrl);
      
      // 添加到生成的海报列表
      const drama = dramas.find(d => d.id === selectedDrama);
      const episode = drama && drama.episodes ? drama.episodes[selectedEpisode] : null;
      
      if (drama && episode) {
        const newPoster = {
          id: Date.now(),
          dramaId: selectedDrama,
          dramaName: drama.name,
          episodeIndex: selectedEpisode,
          episodeTitle: episode.title,
          timestamp: new Date().toISOString(),
          url: screenshotUrl
        };
        
        setGeneratedPosters(prev => [newPoster, ...prev]);
      }
      
      setSnackbarMessage('海报生成成功！');
      setSnackbarSeverity('success');
    } catch (error) {
      console.error('Error capturing poster:', error);
      setSnackbarMessage('海报生成失败');
      setSnackbarSeverity('error');
    } finally {
      setIsGenerating(false);
    }
  };

  // 删除生成的海报
  const handleDeletePoster = (posterId) => {
    setGeneratedPosters(prev => prev.filter(poster => poster.id !== posterId));
  };

  // 保存海报到本地
  const handleSavePoster = (poster) => {
    const link = document.createElement('a');
    link.href = poster.url;
    link.download = `poster_${poster.dramaName}_${poster.episodeTitle}_${new Date(poster.timestamp).getTime()}.jpg`;
    link.click();
  };

  // 保存海报到服务器
  const handleSaveToServer = async (poster) => {
    try {
      const response = await fetch('/api/save-poster', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dramaId: poster.dramaId,
          episodeIndex: poster.episodeIndex,
          imageData: poster.url
        }),
      });

      if (response.ok) {
        setSnackbarMessage('海报已成功保存到服务器');
        setSnackbarSeverity('success');
      } else {
        throw new Error('Failed to save poster');
      }
    } catch (error) {
      console.error('Error saving poster:', error);
      setSnackbarMessage('保存海报到服务器失败');
      setSnackbarSeverity('error');
    }
  };

  // 保存为海报（替换当前集数的缩略图）
  const handleSaveAsPoster = async () => {
    if (!thumbnailUrl || !selectedDrama || selectedEpisode === '' || selectedEpisode === null || selectedEpisode === undefined) {
      setSnackbarMessage('请先选择剧集、集数并拍照');
      setSnackbarSeverity('warning');
      return;
    }

    try {
      const response = await fetch('/api/update-episode-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dramaId: selectedDrama,
          episodeIndex: selectedEpisode,
          imageData: thumbnailUrl
        }),
      });

      if (response.ok) {
        setSnackbarMessage('海报已成功保存为该集缩略图');
        setSnackbarSeverity('success');
        // 重新加载剧集数据
        loadDramas();
      } else {
        throw new Error('Failed to update episode image');
      }
    } catch (error) {
      console.error('Error updating episode image:', error);
      setSnackbarMessage('设置缩略图失败');
      setSnackbarSeverity('error');
    }
  };

  // 获取当前选中的视频URL
  const getCurrentVideoUrl = () => {
    if (!selectedDrama || selectedEpisode === '' || selectedEpisode === null || selectedEpisode === undefined) return '';
    
    const drama = dramas.find(d => d.id === selectedDrama);
    const episode = drama && drama.episodes ? drama.episodes[selectedEpisode] : null;
    
    return episode ? episode.video_url : '';
  };

  // 格式化时间
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 处理视频时间更新
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // 处理视频加载
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setCurrentTime(0);
      setDuration(videoRef.current.duration || 0);
    }
  };

  // 拖动进度条跳转
  const handleSeek = (event, newValue) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ 
      mt: 3, 
      mb: 3
    }}>
      <Paper sx={{ 
        p: 3, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          mb: 3,
          flex: '0 0 auto' // 不伸展，固定高度
        }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>剧集名称</InputLabel>
                <Select
                  value={selectedDrama}
                  label="剧集名称"
                  onChange={handleDramaChange}
                >
                  {dramas.map(drama => (
                    <MenuItem key={drama.id} value={drama.id}>
                      {drama.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!selectedDrama}>
                <InputLabel>集数</InputLabel>
                <Select
                  value={selectedEpisode}
                  label="集数"
                  onChange={handleEpisodeChange}
                  disabled={!selectedDrama}
                >
                  {episodes.map((episode, index) => (
                    <MenuItem key={index} value={index}>
                      {episode.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3, flex: '0 0 auto' }} />

        <Box sx={{ 
          mb: 3,
          flex: '1 1 auto', // 灵活伸展，填充剩余空间
          display: 'flex',
          height: '100%'
        }}>
          {/* 左侧：视频预览与控制 */}
          <Box sx={{ 
            width: '66%',
            mr: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center' // 水平居中
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', // 水平居中
              alignItems: 'center', // 垂直居中
              mb: 3,
              position: 'relative',
              height: '400px', // 固定高度，与截图区域一致
              width: '100%',
              overflow: 'hidden',
              flex: '1 1 auto' // 灵活伸展
            }}>
              {getCurrentVideoUrl() ? (
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#000'
                }}>
                  <video
                    ref={videoRef}
                    src={getCurrentVideoUrl()}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                    }}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  
                  {/* 时间显示 */}
                  {videoRef.current && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      {formatTime(currentTime)} / {formatTime(videoRef.current.duration || 0)}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#f5f5f5',
                  border: '2px dashed #ddd',
                  borderRadius: '8px'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    请先选择剧集和集数
                  </Typography>
                </Box>
              )}
            </Box>

            {/* 进度条 */}
            {getCurrentVideoUrl() && (
              <Box sx={{
                width: '100%',
                px: 1,
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Typography variant="caption" sx={{ minWidth: '50px', fontFamily: 'monospace' }}>
                  {formatTime(currentTime)}
                </Typography>
                <Slider
                  size="small"
                  value={currentTime}
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  onChange={handleSeek}
                  disabled={!duration}
                  sx={{
                    flex: 1,
                    '& .MuiSlider-thumb': {
                      width: 14,
                      height: 14,
                      transition: 'none'
                    },
                    '& .MuiSlider-track': {
                      transition: 'none'
                    }
                  }}
                />
                <Typography variant="caption" sx={{ minWidth: '50px', fontFamily: 'monospace' }}>
                  {formatTime(duration)}
                </Typography>
              </Box>
            )}
            
            {/* 视频控制按钮 */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', // 水平居中
              gap: 2,
              mt: 2
            }}>
              <Button
                variant="contained"
                startIcon={<SkipPreviousIcon />}
                onClick={handlePreviousFrame}
                disabled={!getCurrentVideoUrl() || isGenerating}
                sx={{ minWidth: '100px' }}
              >
                前一帧
              </Button>
              
              <Button
                variant="contained"
                startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                onClick={handlePlayPause}
                disabled={!getCurrentVideoUrl() || isGenerating}
                sx={{ minWidth: '100px' }}
              >
                {isPlaying ? '暂停' : '播放'}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<SkipNextIcon />}
                onClick={handleNextFrame}
                disabled={!getCurrentVideoUrl() || isGenerating}
                sx={{ minWidth: '100px' }}
              >
                后一帧
              </Button>
              
              <Button
                variant="contained"
                startIcon={<CameraIcon />}
                onClick={handleCapture}
                disabled={!getCurrentVideoUrl() || isGenerating}
                sx={{ minWidth: '100px' }}
              >
                拍照
              </Button>
            </Box>
          </Box>

          {/* 右侧：截图预览 */}
          <Box sx={{ 
            width: '34%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center' // 水平居中
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              mb: 3,
              position: 'relative',
              height: '400px', // 固定高度，与视频区域一致
              width: '100%',
              overflow: 'hidden',
              flex: '1 1 auto' // 灵活伸展
            }}>
              {thumbnailUrl ? (
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img
                    src={thumbnailUrl}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'contain', // 保持比例
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                    }}
                    alt="截图预览"
                  />
                </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#f5f5f5',
                  border: '2px dashed #ddd',
                  borderRadius: '8px'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    暂无截图
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* 截图操作按钮 */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', // 水平居中
              gap: 2,
              mt: 2,
              flexWrap: 'wrap'
            }}>
              <Button
                variant="contained"
                onClick={() => handleSavePoster({
                  url: thumbnailUrl,
                  dramaName: dramas.find(d => d.id === selectedDrama)?.name || '未知剧集',
                  episodeTitle: (dramas.find(d => d.id === selectedDrama)?.episodes || [])[selectedEpisode]?.title || '未知集数',
                  timestamp: new Date().toISOString()
                })}
                disabled={!thumbnailUrl}
                sx={{ minWidth: '100px' }}
              >
                保存本地
              </Button>
              <Button
                variant="outlined"
                onClick={handleSaveAsPoster}
                disabled={!thumbnailUrl}
                sx={{ minWidth: '100px' }}
              >
                保存为海报
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {generatedPosters.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              已生成的海报
            </Typography>
            
            <Grid container spacing={3}>
              {generatedPosters.map(poster => (
                <Grid item xs={12} sm={6} md={4} key={poster.id}>
                  <Card sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }}>
                    <CardMedia
                      component="img"
                      height="300"
                      image={poster.url}
                      alt={poster.dramaName + ' - ' + poster.episodeTitle}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" noWrap sx={{ mb: 1 }}>
                        {poster.dramaName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {poster.episodeTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        生成时间: {new Date(poster.timestamp).toLocaleString()}
                      </Typography>
                    </CardContent>
                    <Box sx={{ 
                      p: 2, 
                      display: 'flex', 
                      gap: 1, 
                      justifyContent: 'flex-end'
                    }}>
                      <Button
                        size="small"
                        startIcon={<FolderOpenIcon />}
                        onClick={() => handleSavePoster(poster)}
                      >
                        保存
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleSaveToServer(poster)}
                      >
                        保存到服务器
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeletePoster(poster.id)}
                      >
                        删除
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarMessage('')}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPosterGenerator;